import sheetUrl from 'emoji-datasource-apple/img/apple/sheets/32.png?url'

// Sheet geometry — iamcal emoji-data sheets are 32×32-px sprites with
// 1px of padding around each cell (so the cell stride is 34px), in a
// 62×62 grid → total image is 62×34 = 2108 px on each side. Earlier
// math here assumed no padding and a 1984-px sheet, which scaled and
// offset every sprite enough that clicks showed a neighbouring emoji.
export const TG_SHEET_URL = sheetUrl
export const TG_SHEET_COLS = 62
export const TG_SHEET_ROWS = 62
export const TG_SHEET_CELL = 32
export const TG_SHEET_PAD = 1
export const TG_SHEET_STRIDE = TG_SHEET_CELL + TG_SHEET_PAD * 2 // 34
export const TG_SHEET_PX = TG_SHEET_COLS * TG_SHEET_STRIDE // 2108

interface Props {
  /** Emoji ID — usually the `unified` field (e.g. "1F4AF" or "1F1FA-1F1F8"). */
  unified: string
  /** Display size in CSS pixels. Defaults to 24. */
  size?: number
  /** Optional skin-tone modifier suffix (e.g. "1F3FB"). */
  skin?: string
  /** Sheet column override (when caller already has it). */
  sheetX?: number
  /** Sheet row override. */
  sheetY?: number
  /** Optional title/aria. */
  title?: string
  className?: string
}

/**
 * Renders a single Apple-style emoji from the bundled sprite sheet.
 * Same source font (Apple Color Emoji) that Telegram Desktop uses for
 * its `emoji_{1..8}.webp` atlases — so visually identical, just with
 * a sane web data path (committed JSON + one sheet PNG) instead of
 * the C++ codegen pipeline.
 *
 * Pass either `sheetX`/`sheetY` directly (cheaper — no lookup) or the
 * `unified` codepoint and let the picker populate them upstream.
 */
export default function TgEmoji({ unified, size = 24, sheetX, sheetY, title, className }: Props) {
  if (sheetX === undefined || sheetY === undefined) {
    // Lazy-resolve fallback. The picker pre-resolves these so this
    // path is hit only by callers that pass just a unified string.
    const meta = lookup(unified)
    if (!meta) {
      // Fall back to the native unicode if the codepoint isn't in
      // our dataset (e.g. very-new emoji not yet shipped by Apple).
      return <span className={className} style={{ fontSize: size }}>{unifiedToNative(unified)}</span>
    }
    sheetX = meta.sheet_x
    sheetY = meta.sheet_y
  }

  // Scale the 32-px source sprite to the requested display size, then
  // honour the 1-px inter-sprite padding. Without the padding offset,
  // sub-pixel rounding bleeds the neighbouring emoji into the cell —
  // that was the "I clicked X, got Y" bug.
  const ratio = size / TG_SHEET_CELL
  const bgSize = `${TG_SHEET_PX * ratio}px ${TG_SHEET_PX * ratio}px`
  const bgPos = `-${(sheetX * TG_SHEET_STRIDE + TG_SHEET_PAD) * ratio}px -${
    (sheetY * TG_SHEET_STRIDE + TG_SHEET_PAD) * ratio
  }px`

  return (
    <span
      role="img"
      aria-label={title || unified}
      className={`inline-block align-middle shrink-0 ${className || ''}`}
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${TG_SHEET_URL})`,
        backgroundSize: bgSize,
        backgroundPosition: bgPos,
        backgroundRepeat: 'no-repeat',
        // Helps Chromium pick a sharper resampler when scaling the
        // 32-px source down to e.g. 24-px display.
        imageRendering: ratio < 1 ? 'auto' : 'crisp-edges',
      }}
    />
  )
}

// Convert "1F4AF" or "1F1FA-1F1F8" or "1F468-1F3FB-200D-1F4BB" into
// the corresponding native emoji string. Used as the fallback render
// path and as the value sent to the chat input on selection.
export function unifiedToNative(unified: string): string {
  if (!unified) return ''
  return unified
    .split('-')
    .map((p) => parseInt(p, 16))
    .filter((cp) => !Number.isNaN(cp))
    .map((cp) => String.fromCodePoint(cp))
    .join('')
}

// Cheap one-time lookup map keyed by lowercased unified.
type Meta = { sheet_x: number; sheet_y: number }
let _lookup: Map<string, Meta> | null = null
function lookup(unified: string): Meta | null {
  if (!_lookup) {
    _lookup = new Map()
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const all: any[] = require('emoji-datasource-apple/emoji.json')
    for (const e of all) {
      if (!e.has_img_apple) continue
      _lookup.set(String(e.unified).toLowerCase(), { sheet_x: e.sheet_x, sheet_y: e.sheet_y })
    }
  }
  return _lookup.get(unified.toLowerCase()) || null
}
